import { useEffect, useState, useMemo } from "react";
import { Button } from "../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { useQuery, useMutation } from "@tanstack/react-query";
import { fetchSoapNotes, updateSoapNotes, postToAthena, getEncounterId, addPatientIdAndEncounterIdToSOAPNotes } from "../../api/soap";
import { fetchBillingByAppointment } from "../../api/billingcodes";
import LoadingCard from "./LoadingCard";
import SubjectiveSection from "./sections/SubjectiveSection";
import ObjectiveSection from "./sections/ObjectiveSection";
import AssessmentPlanSection from "./sections/AssessmentPlanSection";
import OrdersSection from "./sections/OrdersSection";
import { X, AlertCircle, CheckCircle2, ClipboardList } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import PrintExportHeader from "../ui/PrintExportHeader";
import HealthProgressNotes from "./healthProgressNotes";
import PostCallFallback from "./PostCallFallback";

const getSoapNotesText = (response) =>
  response?.data?.data?.data?.soap_notes ??
  response?.data?.data?.soap_notes ??
  response?.data?.soap_notes ??
  response?.soap_notes ??
  "";

const SECTION_TITLES = [
  "Procedure Information",
  "Anesthesia / Analgesia",
  "Preparation & Equipment",
  "Procedure Description",
  "Post-Procedure Assessment",
  "Discharge Instructions",
  "Provider Attestation",
];

const createInitialSoapNotes = () => ({
  patient: "",
  subjective: {},
  objective: {},
  assessmentAndPlan: {},
});

const cloneSoapNotes = (notes = createInitialSoapNotes()) => {
  if (typeof window !== "undefined" && typeof window.structuredClone === "function") {
    return window.structuredClone(notes);
  }

  return JSON.parse(JSON.stringify(notes));
};

const hasMeaningfulPhysicalExam = (objective = {}) =>
  Object.values(objective?.physical_exams || {}).some((value) =>
    String(value || "").trim()
  );

const sanitizeSoapNotesForAthenaPost = (notes = createInitialSoapNotes()) => ({
  ...notes,
  objective: hasMeaningfulPhysicalExam(notes.objective) ? notes.objective : {},
});

const ConfirmationModal = ({
  onCancel,
  onConfirm,
  onReplace,
  isPosting,
  itemName,
  alreadyPosted,
}) => {


  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white p-6 rounded-lg shadow-xl border border-gray-200 max-w-sm w-full mx-4 relative">
        <button
          onClick={onCancel}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          disabled={isPosting}
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Post</h3>

        {alreadyPosted ? (
          <>
            <p className="text-gray-700 text-sm mb-2">
              <span className="font-semibold text-orange-600">
                {itemName}
              </span>{" "}
              is already posted to Athena.
            </p>

            <p className="text-gray-600 text-sm mb-6">
              Do you want to post it again or replace the existing post?
            </p>
          </>
        ) : (
          <p className="text-gray-600 mb-6 text-sm">
            Are you sure you want to post{" "}
            <span className="font-semibold text-gray-900">
              {itemName || "this item"}
            </span>{" "}
            to Athena?
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <Button
            onClick={onCancel}
            variant="outline"
            size="sm"
            className="border-gray-300 text-gray-700"
            disabled={isPosting}
          >
            Cancel
          </Button>

          {alreadyPosted ? (
            <>
              <Button
                onClick={() => onConfirm("add")}
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-700"
                disabled={isPosting}
              >
                {isPosting ? "Posting..." : "Post Again"}
              </Button>

              <Button
                onClick={onReplace}
                size="sm"
                className="bg-orange-600 text-white hover:bg-orange-700"
                disabled={isPosting}
              >
                {isPosting ? "Updating..." : "Replace"}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => onConfirm("add")}
              size="sm"
              className="bg-blue-600 text-white hover:bg-blue-700"
              disabled={isPosting}
            >
              {isPosting ? "Posting..." : "Post"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const InitialPostModal = ({ onCancel, onEdit, onProceed }) => {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white p-6 rounded-lg shadow-xl border border-gray-200 max-w-md w-full mx-4 relative">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Post SOAP Note</h3>
        <p className="text-gray-600 mb-8">
          Do you want to post the SOAP note to Athena?
        </p>
        <div className="flex gap-3 justify-end">
          <Button
            onClick={onCancel}
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={onEdit}
            className="bg-yellow-600 text-white hover:bg-yellow-700"
          >
            Edit Note
          </Button>
          <Button
            onClick={onProceed}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            Review & Post
          </Button>
        </div>
      </div>
    </div>
  );
};

const ReviewPage = ({
  soapNotes,
  onCancel,
  onPost,
  onIndividualPost,
  status,
  sectionStatuses,
  postResetKey,
}) => {
  const [selectedActionType, setSelectedActionType] = useState("replace");
  const noOp = () => { };

  const KEY_LABELS = {
    reason: "Reason",
    subjective: "Subjective",
    ros: "Review of Systems",
    objective: "Objective",
    assessmentPlan: "Assessment & Plan",
  };

  const failedSections = Object.keys(sectionStatuses).filter(
    (k) => sectionStatuses[k] === "error"
  );
  const failedSectionsLabels = failedSections.map((k) => KEY_LABELS[k] || k);

  const handlePostAction = (actionType) => {
    setSelectedActionType(actionType);
    onPost(actionType);
  };

  const handleRetryAction = () => {
    onPost(selectedActionType);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">Review SOAP Note</h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50/50">
          <div className="max-w-4xl mx-auto space-y-6">
            {soapNotes.patient && (
              <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <h3 className="text-xs font-semibold uppercase tracking-[0.24em] text-black mb-1">
                  Patient
                </h3>
                <p className="text-xl font-semibold text-slate-900">
                  {soapNotes.patient}
                </p>
              </div>
            )}

            <div className="space-y-6">
              <div className="relative">
                <SubjectiveSection
                  soapNotes={soapNotes}
                  setSoapNotes={noOp}
                  isEditing={false}
                  onPost={onIndividualPost}
                  sectionStatuses={sectionStatuses}
                  postResetKey={postResetKey}
                />
              </div>

              <div className="relative">
                <ObjectiveSection
                  soapNotes={soapNotes}
                  setSoapNotes={noOp}
                  isEditing={false}
                  onPost={onIndividualPost}
                  sectionStatuses={sectionStatuses}
                  postResetKey={postResetKey}
                />
              </div>

              <div className="relative">
                <AssessmentPlanSection
                  soapNotes={soapNotes}
                  setSoapNotes={noOp}
                  isEditing={false}
                  onPost={onIndividualPost}
                  sectionStatuses={sectionStatuses}
                  postResetKey={postResetKey}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white border-t px-6 py-4 flex items-center justify-end gap-4 z-10">
          {status === "success" ? (
            <div className="flex items-center gap-2 text-green-700 font-semibold text-lg bg-green-50 px-6 py-2 rounded-md animate-in fade-in">
              <CheckCircle2 className="w-5 h-5" />
              Successfully Posted!
            </div>
          ) : status === "partial_error" ? (
            <div className="flex items-center gap-4 bg-orange-50 border border-orange-200 px-4 py-3 rounded-md w-full justify-between">
              <div className="flex items-center gap-2 text-orange-700 font-medium">
                <AlertCircle className="w-5 h-5" />
                <span>
                  Failed to post: <b>{failedSectionsLabels.join(", ")}</b>. Please
                  try again.
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={onCancel}
                  variant="outline"
                  className="bg-white text-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRetryAction}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  Retry Failed
                </Button>
              </div>
            </div>
          ) : status === "error" ? (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-red-600 font-semibold bg-red-50 px-4 py-2 rounded-md">
                <AlertCircle className="w-5 h-5" />
                Failed to Post completely
              </div>
              <Button onClick={onCancel} variant="outline">
                Close
              </Button>
              <Button
                onClick={handleRetryAction}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                Retry
              </Button>
            </div>
          ) : (
            <TooltipProvider>
              <Button
                onClick={onCancel}
                variant="outline"
                size="lg"
                className="border-gray-300 text-gray-700"
                disabled={status === "posting"}
              >
                Cancel
              </Button>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => handlePostAction("replace")}
                    size="lg"
                    className="bg-blue-600 text-white hover:bg-blue-700 shadow-sm min-w-[160px]"
                    disabled={status === "posting"}
                  >
                    {status === "posting" && selectedActionType === "replace"
                      ? "Replacing..."
                      : "Replace Existing Note"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Replaces the current SOAP note in the EHR.</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => handlePostAction("add")}
                    size="lg"
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50 shadow-sm min-w-[160px]"
                    disabled={status === "posting"}
                  >
                    {status === "posting" && selectedActionType === "add"
                      ? "Adding..."
                      : "Add to Existing Note"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Keeps the existing note and adds this SOAP note below it.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </div>
  );
};

const ProcedureNotesSection = ({ content }) => {
  if (!content) {
    return (
      <p className="text-sm text-gray-500 italic">No procedure notes available.</p>
    );
  }

  const lines = content
    .split("\n")
    .map((l) => l.trim())
    .filter((line) => line && !line.toLowerCase().includes("procedure_note"));

  const sections = [];
  let current = null;

  lines.forEach((line) => {
    if (SECTION_TITLES.includes(line)) {
      current = { title: line, items: [] };
      sections.push(current);
      return;
    }

    if (!current) {
      current = { title: "Procedure Notes", items: [] };
      sections.push(current);
    }

    current.items.push(line);
  });

  const renderLine = (line, idx) => {
    if (line.includes(":")) {
      const [label, ...rest] = line.split(":");
      const value = rest.join(":").trim();

      return (
        <div key={idx} className="grid grid-cols-12 gap-6 py-2">
          <div className="col-span-4 text-xs font-semibold uppercase tracking-[0.18em] text-black">
            {label}
          </div>
          <div className="col-span-8 text-sm text-black">{value || "—"}</div>
        </div>
      );
    }

    return (
      <p key={idx} className="text-sm text-black leading-relaxed py-1">
        {line}
      </p>
    );
  };

  return (
    <div className="space-y-4">
      {sections.map((section, idx) => (
        <div key={idx} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
          <div>
            <h4 className="text-xl font-semibold text-slate-900">
              {section.title}
            </h4>
          </div>
          {section.title === "Procedure Description" ? (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2">
              {section.items.map((line, i) => (
                <p key={i} className="text-sm text-black leading-relaxed">
                  {line}
                </p>
              ))}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {section.items.map((line, i) => renderLine(line, i))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Soap = ({
  appointmentId,
  username,
  appointment,
  duration = "",
  canEdit = true,
  canPostToAthena = false,
  callEndTime,
  postCallWindowExpired = false,
  finalFetchStatus = "idle",
}) => {
  const { toast } = useToast();
  const [soapNotes, setSoapNotes] = useState(createInitialSoapNotes);
  const [draftSoapNotes, setDraftSoapNotes] = useState(createInitialSoapNotes);
  const [manualRetrying, setManualRetrying] = useState(false);
  const [procedureNotes, setProcedureNotes] = useState("");
  const [ordersData, setOrdersData] = useState({ orders: [], confirmed: false });
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("soap");
  const [encounterId, setEncounterId] = useState(appointment?.athena_encounter_id || "");
  const [pendingPost, setPendingPost] = useState(null);
  const [postFlowStage, setPostFlowStage] = useState("idle");
  const [mainPostStatus, setMainPostStatus] = useState("idle");
  const [sectionStatuses, setSectionStatuses] = useState({});
  const [isFullyPosted, setIsFullyPosted] = useState(false);
  const [postResetKey, setPostResetKey] = useState(0);

  const stripControlChars = useMemo(
    () => (value = "") =>
      Array.from(value)
        .filter((ch) => ch.charCodeAt(0) > 31)
        .join(""),
    []
  );

  const isAthenaAppointment = useMemo(() => {
    const hasAthenaIds = Boolean(
      appointment?.athena_encounter_id && appointment?.athena_practice_id
    );
    const athenaLikeCallId = String(appointmentId || "")
      .toLowerCase()
      .startsWith("athena");
    return hasAthenaIds || athenaLikeCallId;
  }, [appointment, appointmentId]);

  const callEndTimestamp = callEndTime ? new Date(callEndTime).getTime() : NaN;
  const deadlineExpired =
    postCallWindowExpired ||
    (Number.isFinite(callEndTimestamp) &&
      Date.now() >= callEndTimestamp + 10 * 60 * 1000);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["soap-notes", appointmentId, username],
    queryFn: async ({ signal }) => {
      const response = await fetchSoapNotes(
        `${username}_${appointmentId}_soap`,
        username,
        signal
      );
      if (!String(getSoapNotesText(response) || "").trim()) {
        throw new Error("SOAP notes are not available yet.");
      }
      return response;
    },
    enabled: Boolean(username && appointmentId && !deadlineExpired),
    retry: 3,
    staleTime: Infinity,
    gcTime: Infinity,
    retryOnMount: true,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const handleRetry = async () => {
    if (deadlineExpired || manualRetrying) return;

    setManualRetrying(true);

    try {
      await refetch();
    } finally {
      setManualRetrying(false);
    }
  };

  const soapText = String(getSoapNotesText(data) || "");
  const hasSoapData = Boolean(soapText.trim());
  const finalFetchPending =
    deadlineExpired && finalFetchStatus === "fetching";

  const { data: billingData } = useQuery({
    queryKey: ["billing-codes", appointmentId, username],
    queryFn: ({ signal }) =>
      fetchBillingByAppointment(`${username}_${appointmentId}_billing`, username,
        signal
      ),
    enabled: Boolean(username && appointmentId && !deadlineExpired),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 3,
    retryOnMount: true,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchInterval: false,
  });

  const extractBlock = (raw, marker, nextMarkers = []) => {
    const idx = raw.indexOf(marker);
    if (idx === -1) return "";
    const start = idx + marker.length;
    const after = raw.slice(start);
    const nextIdxs = nextMarkers
      .map((m) => after.indexOf(m))
      .filter((n) => n !== -1);
    const end = nextIdxs.length ? Math.min(...nextIdxs) : after.length;
    return after.slice(0, end).trim();
  };

  useEffect(() => {
    const getEncounterID = async () => {
      if (isAthenaAppointment && (appointment?.athena_encounter_id === "" || !appointment?.athena_encounter_id)) {
        const response = await getEncounterId(appointmentId, username, appointment?.appointment_date, appointment?.athena_practice_id);
        if (response?.success === true) {
          setEncounterId(response.encounterId);
        }
      }
    }
    getEncounterID();
  }, [appointmentId, appointment, username, isAthenaAppointment]);

  useEffect(() => {
    const addPatientAndEncounterIds = async () => {
      if (!isAthenaAppointment || appointment?.EMR === undefined) return;
      if (!encounterId) return;
      if (data?.data?.ehr_patient_id && data?.data?.ehr_encounter_id) {
        return;
      }
      const response = await addPatientIdAndEncounterIdToSOAPNotes(appointmentId, username, encounterId, appointment?.athena_patient_id);
      if (response?.success === true) {
        console.log("Successfully added patient and encounter IDs to SOAP notes");
      }
    }
    addPatientAndEncounterIds();
  }, [encounterId, appointmentId, username, appointment, isAthenaAppointment, data]);

  useEffect(() => {
    if (isLoading || !data) return;

    let rawData = null;
    if (data?.data?.data?.data?.soap_notes) rawData = data.data.data.data;
    else if (data?.data?.data?.soap_notes) rawData = data.data.data;
    else if (data?.data?.soap_notes) rawData = data.data;
    else if (data?.soap_notes) rawData = data;

    if (!rawData?.soap_notes) return;

    console.log("SOAP API DATA:", data);
    console.log("SOAP RAW:", rawData.soap_notes);

    const raw = rawData.soap_notes;

    if (rawData?.procedure_notes) {
      setProcedureNotes(rawData.procedure_notes);
    } else {
      const procText = raw.includes("$procedure_notes -")
        ? extractBlock(raw, "$procedure_notes -", ["$orders -"])
        : "";
      setProcedureNotes(procText);
    }

    const soapText = raw.includes("$soap_notes -")
      ? extractBlock(raw, "$soap_notes -", ["$procedure_notes -", "$orders -"])
      : raw.split("$procedure_notes -")[0]?.trim() || raw;

    if (rawData?.orders && Array.isArray(rawData.orders)) {
      setOrdersData({ orders: rawData.orders, confirmed: true });
    } else if (raw.includes("$orders -")) {
      const afterOrders = raw.split("$orders -")[1] || "";
      try {
        const cleaned = afterOrders
          .trim()
          .replace(/'/g, '"')
          .replace(/None/g, "null")
          .replace(/True/g, "true")
          .replace(/False/g, "false");
        const parsed = JSON.parse(stripControlChars(cleaned));
        setOrdersData(
          Array.isArray(parsed)
            ? { orders: parsed, confirmed: true }
            : parsed?.orders
              ? parsed
              : { orders: [], confirmed: false }
        );
      } catch {
        setOrdersData({ orders: [], confirmed: false });
      }
    }

    const patientMatch = soapText.match(/Patient:\s*(.*?)\n/);
    const reasonMatch = soapText.match(
      /Reason for Visit(?:\s*-)?([\s\S]*?)(?=\n\nSubjective(?:\s*-)?)/
    );
    const subjectiveMatch = soapText.match(
      /Subjective(?:\s*-)?([\s\S]*?)(?=\n\nFamily history discussed|\n\nSurgical history discussed|\n\nSocial history discussed|\n\nReview of Systems|\n\nObjective(?:\s*-)?)/
    );
    const familyHistoryMatch = soapText.match(
      /Family history discussed in this appointment(?:\s*-)?([\s\S]*?)(?=\n\nSurgical history discussed|\n\nSocial history discussed|\n\nReview of Systems|\n\nObjective(?:\s*-)?)/
    );
    const surgicalHistoryMatch = soapText.match(
      /Surgical history discussed in this appointment(?:\s*-)?([\s\S]*?)(?=\n\nSocial history discussed|\n\nReview of Systems|\n\nObjective(?:\s*-)?)/
    );
    const socialHistoryMatch = soapText.match(
      /Social history discussed in this appointment(?:\s*-)?([\s\S]*?)(?=\n\nReview of Systems|\n\nObjective(?:\s*-)?)/
    );
    const rosMatch = soapText.match(
      /Review of Systems(?:\s*\(ROS\))?(?:\s*[-:])?\s*([\s\S]*?)(?=\n\nObjective(?:\s*-)?)/
    );
    const objectiveMatch = soapText.match(
      /Objective(?:\s*-)?([\s\S]*?)(?=\n\nAssessment and Plan(?:\s*-)?)/
    );
    const assessmentPlanMatch = soapText.match(
      /Assessment and Plan(?:\s*-)?([\s\S]*)$/
    );

    let objectiveJSON = {};
    let assessmentPlanJSON = {};

    try {
      const objRaw = objectiveMatch?.[1]?.trim();
      if (objRaw?.includes("{")) {
        objectiveJSON = JSON.parse(
          stripControlChars(
            objRaw.slice(objRaw.indexOf("{"), objRaw.lastIndexOf("}") + 1)
          ).trim()
        );
      }
    } catch { }

    try {
      const apRaw = assessmentPlanMatch?.[1]?.trim();
      if (apRaw?.includes("{")) {
        assessmentPlanJSON = JSON.parse(
          stripControlChars(
            apRaw.slice(apRaw.indexOf("{"), apRaw.lastIndexOf("}") + 1)
          ).trim()
        );
      }
    } catch { }

    const nextSoapNotes = {
      patient: patientMatch?.[1] || "",
      subjective: {
        chief_complaint: (reasonMatch?.[1] || "").trim(),
        hpi: (subjectiveMatch?.[1] || "").trim(),
        family_history: (familyHistoryMatch?.[1] || "").trim(),
        surgical_history: (surgicalHistoryMatch?.[1] || "").trim(),
        social_history: (socialHistoryMatch?.[1] || "").trim(),
        ros: (rosMatch?.[1] || "").trim(),
      },
      objective: objectiveJSON,
      assessmentAndPlan: assessmentPlanJSON,
    };
    console.log("===== PARSED SOAP =====");
    console.log(nextSoapNotes);
    console.log("OBJECTIVE:", nextSoapNotes.objective);
    console.log("SUBJECTIVE:", nextSoapNotes.subjective);
    console.log("ASSESSMENT:", nextSoapNotes.assessmentAndPlan);


    setSoapNotes(nextSoapNotes);

    if (!isEditing) {
      setDraftSoapNotes(cloneSoapNotes(nextSoapNotes));
    }
  }, [data, isEditing, isLoading, stripControlChars]);

  const [saveSuccess, setSaveSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: ({ updatedNotes }) =>
      updateSoapNotes(`${username}_${appointmentId}_soap`, username, updatedNotes),
    onSuccess: (_, variables) => {
      const nextSoapNotes = cloneSoapNotes(variables?.nextSoapNotes);

      setSoapNotes(nextSoapNotes);
      setDraftSoapNotes(cloneSoapNotes(nextSoapNotes));
      refetch();
      setIsEditing(false);
      setIsFullyPosted(false);
      setSaveSuccess(true);
      toast({
        title: "Practice Intelligence Saved",
        description: "SOAP notes updated successfully in backend database.",
      });
      setTimeout(() => setSaveSuccess(false), 5000);
    },
  });

  const postMutation = useMutation({
    mutationFn: (itemToPost) => postToAthena(itemToPost),
  });

  const mainPostMutation = useMutation({
    mutationFn: (fullData) => postToAthena(fullData),
    onSuccess: (responseData) => {
      const results =
        responseData?.section_results || responseData?.data || responseData;

      if (
        results &&
        (results.reason !== undefined ||
          results.subjective !== undefined ||
          results.ros !== undefined ||
          results.objective !== undefined ||
          results.assessmentPlan !== undefined)
      ) {
        const mapped = {};
        Object.keys(results).forEach((k) => {
          const v = results[k];
          if (v === true) mapped[k] = "success";
          else if (v === false) mapped[k] = "error";
          else mapped[k] = v;
        });

        const humanMapped = {};
        if (mapped.reason) humanMapped["Chief Complaint"] = mapped.reason;
        if (mapped.subjective) {
          humanMapped["History of Present Illness"] = mapped.subjective;
        }
        if (mapped.ros) humanMapped["Review of Systems"] = mapped.ros;
        if (mapped.objective) humanMapped["Physical Exam"] = mapped.objective;
        if (mapped.assessmentPlan) {
          humanMapped["Assessment & Plan"] = mapped.assessmentPlan;
        }

        setSectionStatuses((prev) => ({ ...prev, ...mapped, ...humanMapped }));

        const hasErrors = Object.values(mapped).includes("error");
        setMainPostStatus(hasErrors ? "partial_error" : "success");

        if (!hasErrors) {
          setIsFullyPosted(true);
        }
        return;
      }

      setSectionStatuses((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          if (next[k] === "posting") next[k] = "error";
        });
        return next;
      });
      setMainPostStatus("error");
      toast({
        title: "Failed to verify SOAP post",
        description: "The server response did not include section results.",
      });
    },
    onError: (error) => {
      setSectionStatuses((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          if (next[k] === "posting") next[k] = "error";
        });
        return next;
      });
      setMainPostStatus("error");
      toast({
        title: "Failed to post SOAP",
        description: error?.message || "SOAP post failed",
      });
    },
  });

  const handleInitiatePost = (payload, onSuccess, onError) => {
    const finalPayload = {
      ...payload,
      username,
      athena_encounter_id: encounterId,
      practiceID: appointment?.athena_practice_id,
    };

    setPendingPost({
      data: finalPayload,
      onSuccess,
      onError,
    });
  };

  const handleConfirmPost = (postType = "add") => {
    if (!pendingPost) return;

    const postData = {
      ...pendingPost.data,
      postType,
    };

    postMutation.mutate(postData, {
      onSuccess: () => {
        pendingPost.onSuccess?.();
        setPendingPost(null);
      },
      onError: (error) => {
        pendingPost.onError?.();
        toast({
          title: `Failed to post ${pendingPost.data?.type || "section"}`,
          description: error?.message || "SOAP section post failed",
        });
        setPendingPost(null);
      },
    });
  };

  const handleReplacePost = () => {
    if (!pendingPost) return;

    const replacePayload = {
      ...pendingPost.data,
      postType: "replace",
    };

    postMutation.mutate(replacePayload, {
      onSuccess: () => {
        pendingPost.onSuccess?.();
        setPendingPost(null);

        toast({
          title: "SOAP section updated",
          description: `${pendingPost.data?.type || "Section"} was replaced successfully.`,
        });
      },
      onError: (error) => {
        pendingPost.onError?.();
        toast({
          title: `Failed to post ${pendingPost.data?.type || "section"}`,
          description: error?.message || "SOAP section update failed",
        });
        setPendingPost(null);
      },
    });
  };

  const handleCancelPost = () => {
    setPendingPost(null);
    setPostResetKey((prev) => prev + 1);
    setSectionStatuses((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (next[key] === "posting") next[key] = "idle";
      });
      return next;
    });
  };

  const handleMainPostClick = () => {
    setPostFlowStage("confirming");
  };

  const handleInitialConfirmEdit = () => {
    setPostFlowStage("idle");
    setDraftSoapNotes(cloneSoapNotes(soapNotes));
    setIsEditing(true);
  };

  const handleInitialConfirmProceed = () => {
    setPostFlowStage("reviewing");
  };

  const handleInitialConfirmCancel = () => {
    setPostFlowStage("idle");
    setMainPostStatus("idle");
    setSectionStatuses({});
  };

  const handleReviewPost = (postType = "replace") => {
    setMainPostStatus("posting");

    const athenaSoapNotes = sanitizeSoapNotesForAthenaPost(soapNotes);
    const activeStatuses = {};
    if (soapNotes.subjective.chief_complaint) activeStatuses.reason = "posting";
    if (soapNotes.subjective.hpi) activeStatuses.subjective = "posting";
    if (soapNotes.subjective.ros) activeStatuses.ros = "posting";
    if (hasMeaningfulPhysicalExam(athenaSoapNotes.objective)) {
      activeStatuses.objective = "posting";
    }

    const ap = athenaSoapNotes.assessmentAndPlan;
    if ((ap?.problems && ap.problems.length > 0) || ap?.follow_up) {
      activeStatuses.assessmentPlan = "posting";
    }

    const humanMap = {};
    if (activeStatuses.reason) humanMap["Chief Complaint"] = activeStatuses.reason;
    if (activeStatuses.subjective) {
      humanMap["History of Present Illness"] = activeStatuses.subjective;
    }
    if (activeStatuses.ros) humanMap["Review of Systems"] = activeStatuses.ros;
    if (activeStatuses.objective) humanMap["Physical Exam"] = activeStatuses.objective;
    if (activeStatuses.assessmentPlan) {
      humanMap["Assessment & Plan"] = activeStatuses.assessmentPlan;
    }

    setSectionStatuses({ ...activeStatuses, ...humanMap });

    const fullRaw = buildFullRaw(athenaSoapNotes);
    mainPostMutation.mutate({
      content: fullRaw,
      username,
      athena_encounter_id: encounterId,
      practiceID: appointment?.athena_practice_id,
      postType,
    });
  };

  const handleReviewCancel = () => {
    setPostFlowStage("idle");
    setMainPostStatus("idle");
    setSectionStatuses({});
    setPostResetKey((prev) => prev + 1);
  };

  const buildRawSoap = useMemo(() => {
    return (state) => {
      const {
        patient,
        subjective: {
          chief_complaint,
          hpi,
          family_history,
          surgical_history,
          social_history,
          ros,
        },
        objective,
        assessmentAndPlan,
      } = state;

      return [
        patient ? `Patient: ${patient}` : "",
        "",
        chief_complaint ? `Reason for Visit - ${chief_complaint}` : "",
        "",
        `Subjective - ${hpi || ""}`,
        "",
        `Family history discussed in this appointment - ${family_history || "Not discussed"
        }`,
        "",
        `Surgical history discussed in this appointment - ${surgical_history || "Not discussed"
        }`,
        "",
        `Social history discussed in this appointment - ${social_history || "Not discussed"
        }`,
        "",
        ros ? `Review of Systems:\n${ros}` : "Review of Systems:\n",
        "",
        `Objective - ${JSON.stringify(objective || {}, null, 2)}`,
        "",
        `Assessment and Plan - ${JSON.stringify(assessmentAndPlan || {}, null, 2)}`,
      ].join("\n");
    };
  }, []);

  const buildFullRaw = (state) => {
    const soapOnly = buildRawSoap(state);
    return [
      `$soap_notes -\n${soapOnly}`,
      `$procedure_notes -\n${procedureNotes || ""}`,
      `$orders - ${JSON.stringify(ordersData)}`,
    ].join("\n\n");
  };

  const handleSave = async () => {
    const nextSoapNotes = cloneSoapNotes(draftSoapNotes);
    const rawOut = buildFullRaw(nextSoapNotes);
    mutation.mutate({ updatedNotes: rawOut, nextSoapNotes });
  };

  const handleCancel = () => {
    setDraftSoapNotes(cloneSoapNotes(soapNotes));
    setIsEditing(false);
  };

  if (isLoading || isFetching || manualRetrying || finalFetchPending) {
    return <LoadingCard message="Loading SOAP..." />;
  }

  if (!hasSoapData) {
    return (
      <PostCallFallback
        title="SOAP Notes"
        icon={<ClipboardList size={20} />}
        message={
          deadlineExpired
            ? "SOAP notes are not available."
            : "SOAP notes are not available yet. You can try again."
        }
        deadlineExpired={deadlineExpired}
        onRetry={handleRetry}
        isRetrying={manualRetrying || isFetching}
        retryLabel="Retry SOAP"
        retryingLabel="Retrying SOAP…"
      />
    );
  }


  return (
    <div className="relative space-y-6 text-gray-900 leading-snug">
      {isAthenaAppointment && canPostToAthena && pendingPost && (
        <ConfirmationModal
          onCancel={handleCancelPost}
          onConfirm={handleConfirmPost}
          onReplace={handleReplacePost}
          isPosting={postMutation.isPending || postMutation.isLoading}
          itemName={pendingPost.data?.type}
          alreadyPosted={pendingPost.data?.alreadyPosted}
        />
      )}

      {isAthenaAppointment && canPostToAthena && postFlowStage === "confirming" && (
        <InitialPostModal
          onCancel={handleInitialConfirmCancel}
          onEdit={handleInitialConfirmEdit}
          onProceed={handleInitialConfirmProceed}
        />
      )}

      {isAthenaAppointment && canPostToAthena && postFlowStage === "reviewing" && (
        <ReviewPage
          soapNotes={soapNotes}
          onCancel={handleReviewCancel}
          onPost={handleReviewPost}
          onIndividualPost={handleInitiatePost}
          status={mainPostStatus}
          sectionStatuses={sectionStatuses}
          postResetKey={postResetKey}
        />
      )}

      <h3 className="font-semibold text-black text-lg">SOAP Notes</h3>

      <div className="flex gap-3">
        {["soap", "healthProcessNotes", "procedure", "orders"].map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-5 py-2 rounded-md text-sm font-medium border ${activeTab === t
              ? "bg-blue-600 text-white border-blue-600"
              : "bg-white text-black-700 border-black-300"
              }`}
          >
            {t === "soap" ? "SOAP" : t === "procedure" ? "Procedure Notes" : t === "healthProcessNotes" ? "Behavioral Health Progress Note" : "Orders"}
          </button>
        ))}
      </div>

      {activeTab === "soap" ? (
        <>
          <PrintExportHeader title="Practice Intelligence Clinical SOAP Record" subtitle="Encounter Summary & Notes" />
          <div className="space-y-6 divide-y divide-gray-300">
            {soapNotes.patient && (
              <div className="pb-2">
                <p className="text-base font-medium text-gray-900">
                  {soapNotes.patient}
                </p>
              </div>
            )}

            <SubjectiveSection
              soapNotes={isEditing ? draftSoapNotes : soapNotes}
              setSoapNotes={isEditing ? setDraftSoapNotes : setSoapNotes}
              isEditing={isEditing}
              onPost={isAthenaAppointment && canPostToAthena ? handleInitiatePost : undefined}
              sectionStatuses={sectionStatuses}
              postResetKey={postResetKey}
            />

            <ObjectiveSection
              soapNotes={isEditing ? draftSoapNotes : soapNotes}
              setSoapNotes={isEditing ? setDraftSoapNotes : setSoapNotes}
              isEditing={isEditing}
              onPost={isAthenaAppointment && canPostToAthena ? handleInitiatePost : undefined}
              sectionStatuses={sectionStatuses}
              postResetKey={postResetKey}
            />

            <AssessmentPlanSection
              soapNotes={isEditing ? draftSoapNotes : soapNotes}
              setSoapNotes={isEditing ? setDraftSoapNotes : setSoapNotes}
              isEditing={isEditing}
              onPost={isAthenaAppointment && canPostToAthena ? handleInitiatePost : undefined}
              sectionStatuses={sectionStatuses}
              postResetKey={postResetKey}
            />
          </div>

          <div className="pt-4 border-t border-gray-300 space-y-3">
            {isAthenaAppointment && canPostToAthena && isFullyPosted && !isEditing && (
              <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>
                  SOAP note posted to Athena. Edit the note and save to enable reposting.
                </span>
              </div>
            )}

            <div className="flex justify-end items-center gap-3 no-print">
              {saveSuccess && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold animate-in fade-in transition-all">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Saved
                </span>
              )}

              {!isEditing ? (
                <>
                  {canEdit && (
                    <Button
                      onClick={() => {
                        setDraftSoapNotes(cloneSoapNotes(soapNotes));
                        setIsEditing(true);
                      }}
                      className="bg-yellow-600 text-white hover:bg-yellow-700"
                    >
                      Edit
                    </Button>
                  )}
                  {isAthenaAppointment && canPostToAthena && (
                    <Button
                      onClick={handleMainPostClick}
                      disabled={isFullyPosted}
                      className={
                        isFullyPosted
                          ? "bg-gray-400 text-white cursor-not-allowed hover:bg-gray-400"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      }
                      title={
                        isFullyPosted
                          ? "SOAP already posted. Edit the note to repost."
                          : ""
                      }
                    >
                      {isFullyPosted ? "SOAP Already Posted" : "Post Soap to EHR"}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button
                    onClick={handleSave}
                    disabled={mutation.isPending || mutation.isLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {mutation.isPending || mutation.isLoading
                      ? "Saving..."
                      : "Save SOAP Notes"}
                  </Button>
                  <Button
                    onClick={handleCancel}
                    className="bg-gray-500 hover:bg-gray-600 text-white"
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          </div>
        </>
      ) : activeTab === "healthProcessNotes" ? (
        <HealthProgressNotes
          appointmentId={appointment?.id}
          username={appointment?.doctor_email}
          duration={duration}
          canEdit={canEdit}
          canPostToAthena={canPostToAthena}
        />
      ) : activeTab === "procedure" ? (
        <ProcedureNotesSection content={procedureNotes} />
      ) : (
        <OrdersSection
          ordersData={ordersData}
          billingData={billingData}
          soapNotes={soapNotes}
          doctorEmail={appointment?.doctor_email}
          encounterId={encounterId}
          practiceId={appointment?.athena_practice_id}
          appointmentId={appointment?.id}
          canPostToAthena={isAthenaAppointment && canPostToAthena}
        />
      )}
    </div>
  );
};

export default Soap
